import { useCallback } from "react";
import invariant from "tiny-invariant";
import { Country, Currency } from "@lookiero/sty-psp-locale";
import { Segment } from "@lookiero/sty-psp-segment";
import { Tradename } from "@lookiero/sty-sp-tradename";
import { CheckoutItemStatus } from "../../domain/checkoutItem/model/checkoutItem";
import { CheckoutProjection } from "../../projection/checkout/checkout";
import { CheckoutPricingProjection } from "../../projection/pricing/pricing";
import { PROJECT, TrackingPage } from "./tracking";
import { CheckoutTrackingEvent, TrackingEventName, TrackingEventCategory } from "./tracking";

interface TrackCheckoutFunction {
  (): CheckoutTrackingEvent;
}

interface UseTrackCheckoutFunctionArgs {
  readonly checkout: CheckoutProjection | undefined;
  readonly pricing: CheckoutPricingProjection | undefined;
  readonly userId: string;
  readonly country: Country;
  readonly segment: Segment;
  readonly tradename: Tradename;
}

interface UseTrackCheckoutFunction {
  (args: UseTrackCheckoutFunctionArgs): TrackCheckoutFunction;
}

const useTrackCheckout: UseTrackCheckoutFunction = ({ checkout, pricing, segment, country, userId, tradename }) => {
  const trackCheckout: TrackCheckoutFunction = useCallback(() => {
    invariant(checkout, "Checkout must be defined to track checkout");

    const checkoutItemsKept = checkout.items.filter(
      (checkoutItem) =>
        checkoutItem.status === CheckoutItemStatus.KEPT || checkoutItem.status === CheckoutItemStatus.REPLACED,
    );
    const totalReplacedFor = checkoutItemsKept?.filter(({ replacedFor }) => Boolean(replacedFor)).length || 0;

    const checkoutTrackingEvent: CheckoutTrackingEvent = {
      event: TrackingEventName.CHECKOUT,
      eventCategory: TrackingEventCategory.ECOMMERCE,
      section: `${PROJECT}_${TrackingPage.CHECKOUT}`,
      store: country,
      segment,
      checkoutId: checkout.id,
      userId,
      tradename,
      sizeChanges: totalReplacedFor,
      ecommerce: {
        checkout: {
          actionField: {
            items: checkoutItemsKept.length || 0,
            currencyCode: pricing?.currency as Currency,
            value: (pricing?.finalPrice as number) / 100,
          },
        },
      },
    };

    return checkoutTrackingEvent;
  }, [checkout, country, pricing?.finalPrice, pricing?.currency, segment, userId, tradename]);

  return trackCheckout;
};

export { useTrackCheckout };
